import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {flagsApi, type Flag, type FlagColor} from './flags';
import {isItemsPageQuery} from './hooks';
import {itemsApi, type ItemsPage} from './items';

const FLAGS_KEY = ['me', 'flags'] as const;

// The caller's eight flags with the name to show for each. Cheap and rarely changes, so it is
// fetched once and shared by every picker on the page.
export const useFlags = () =>
  useQuery({
    queryKey: FLAGS_KEY,
    queryFn: async () => flagsApi.list(),
    staleTime: 5 * 60_000
  });

// Names (or, blank, un-names) each listed flag. One write per colour, so only what was changed is
// sent and a flag left alone can never be reset by saving another.
export const useRenameFlags = () => {
  const qc = useQueryClient();
  return useMutation({
    async mutationFn(renames: ReadonlyArray<{color: FlagColor; label: string | null}>) {
      await Promise.all(renames.map(async (r) => flagsApi.rename(r.color, r.label)));
    },
    onSuccess(_data, renames) {
      qc.setQueryData<Flag[]>(FLAGS_KEY, (flags) =>
        flags?.map((flag) => {
          const rename = renames.find((r) => r.color === flag.color);
          return rename
            ? {...flag, label: rename.label, name: rename.label ?? flag.default_name}
            : flag;
        })
      );
    },
    // A partial failure leaves some names saved; refetch so the picker shows what the server has.
    onSettled() {
      void qc.invalidateQueries({queryKey: FLAGS_KEY});
    }
  });
};

/**
 Sets (or clears) the caller's note on their flag for one item, patched into every cached item page
 first like useSetItemFlags, and rolled back if the write fails.
 */
export const useSetFlagNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({itemId, note}: {itemId: string; note: string | null}) =>
      itemsApi.setFlagNote(itemId, note),
    async onMutate({itemId, note}) {
      await qc.cancelQueries({predicate: isItemsPageQuery});
      const previous = qc.getQueriesData<ItemsPage>({predicate: isItemsPageQuery});
      const trimmed = note?.trim() ? note.trim() : null;
      qc.setQueriesData<ItemsPage>({predicate: isItemsPageQuery}, (page) =>
        page
          ? {
              ...page,
              results: page.results.map((item) =>
                item.id === itemId ? {...item, flag_note: trimmed} : item
              )
            }
          : page
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    }
  });
};

/**
 Flags one item with a note in a single write ("Flag with note…"), patched into every cached item
 page first like useSetItemFlags and rolled back if the write fails.
 */
export const useFlagWithNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      color,
      note
    }: {
      itemId: string;
      color: FlagColor;
      note: string | null;
    }) => itemsApi.setFlags([itemId], color, note),
    async onMutate({itemId, color, note}) {
      await qc.cancelQueries({predicate: isItemsPageQuery});
      const previous = qc.getQueriesData<ItemsPage>({predicate: isItemsPageQuery});
      qc.setQueriesData<ItemsPage>({predicate: isItemsPageQuery}, (page) =>
        page
          ? {
              ...page,
              results: page.results.map((item) =>
                item.id === itemId
                  ? {...item, flag: color, flag_note: note ?? item.flag_note ?? null}
                  : item
              )
            }
          : page
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    }
  });
};
